/**
 * ResumeWorkspaceHome — /resume-mastery/workspace
 *
 * The authenticated hub for the Resume Workspace. Owns the usage query and
 * renders the UsageMeter at the top; below it sits the master-profile builder
 * (PB-T4) and the targets list + create flow (PB-T5).
 *
 * Targets are gated behind the profile — you can't tailor to a role until you
 * have a master profile to tailor. Creating a target can return a 429 when a
 * free-tier user hits their target cap; we surface that inline as an upgrade
 * prompt rather than a generic error.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  ResumeTarget,
  ResumeTargetStatus,
  createResumeTarget,
  getResumeProfile,
  getResumeUsage,
  listResumeTargets,
} from "../api/client";
import ProfileBuilder from "../components/resume/ProfileBuilder";
import UsageMeter from "../components/resume/UsageMeter";

// coral via theme.palette.primary — per-product accent deferred (spec D2)
const MIN_JD_CHARS = 100;

/** Quota 429 codes from the targets endpoint. */
const QUOTA_CODES = new Set(["RESUME_TARGET_LIMIT", "RESUME_QUOTA_EXCEEDED"]);

/** Status → Chip color, so the list reads at a glance. */
const STATUS_COLORS: Record<ResumeTargetStatus, "default" | "info" | "warning" | "success" | "error"> = {
  researching: "default",
  applied: "info",
  interviewing: "warning",
  offer: "success",
  rejected: "error",
};

export default function ResumeWorkspaceHome() {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const queryClient = useQueryClient();
  const { data: usage, isLoading } = useQuery({
    queryKey: ["resume", "usage"],
    queryFn: getResumeUsage,
  });
  const { data: profile } = useQuery({
    queryKey: ["resume", "profile"],
    queryFn: getResumeProfile,
  });

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 12, textAlign: "center" }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t("ResumeWorkspaceHome.loadingWorkspace")}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Stack spacing={4}>
        <Box>
          <Typography
            variant="overline"
            sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.1em" }}
          >
            {t("ResumeWorkspaceHome.eyebrow")}
          </Typography>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mt: 0.5 }}>
            {t("ResumeWorkspaceHome.heading")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 640, lineHeight: 1.65 }}>
            {t("ResumeWorkspaceHome.subheading")}
          </Typography>
        </Box>

        {usage && <UsageMeter usage={usage} />}

        <ProfileBuilder
          profile={profile ?? null}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ["resume", "profile"] });
            queryClient.invalidateQueries({ queryKey: ["resume", "usage"] });
          }}
        />

        <Targets hasProfile={!!profile} />
      </Stack>
    </Container>
  );
}

/* -------------------------------------------------------------------------- */
/* Targets — list + create, gated behind a master profile                     */
/* -------------------------------------------------------------------------- */

/** Pull our structured {code, message} detail off an axios error, with fallback. */
function targetErrorMessage(err: any, t: TFunction): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || t("ResumeWorkspaceHome.createFailedFallback");
}

function Targets({ hasProfile }: { hasProfile: boolean }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages-account");

  const { data: targets } = useQuery({
    queryKey: ["resume", "targets"],
    queryFn: listResumeTargets,
    enabled: hasProfile,
  });

  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");

  const createMutation = useMutation({
    mutationFn: createResumeTarget,
    onSuccess: (target) => {
      queryClient.invalidateQueries({ queryKey: ["resume", "targets"] });
      queryClient.invalidateQueries({ queryKey: ["resume", "usage"] });
      navigate(`/resume-mastery/workspace/targets/${target.id}`);
    },
  });

  // The gate: no master profile, no targets. You tailor *from* a profile.
  if (!hasProfile) {
    return (
      <Card
        variant="outlined"
        sx={{ borderColor: alpha(theme.palette.primary.light, 0.2), borderRadius: 3, borderStyle: "dashed" }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 }, textAlign: "center" }}>
          <Typography variant="subtitle1" fontWeight={500}>
            {t("ResumeWorkspaceHome.buildProfileFirstHeading")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("ResumeWorkspaceHome.buildProfileFirstBody")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const err = createMutation.error as any;
  const isQuotaError =
    err?.response?.status === 429 &&
    QUOTA_CODES.has(err?.response?.data?.detail?.code);

  const jdTooShort = jobDescription.trim().length < MIN_JD_CHARS;
  const canCreate =
    company.trim().length > 0 && roleTitle.trim().length > 0 && !jdTooShort;

  const handleCreate = () => {
    if (!canCreate) return;
    createMutation.mutate({
      company: company.trim(),
      role_title: roleTitle.trim(),
      job_description: jobDescription.trim(),
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{ borderColor: alpha(theme.palette.primary.light, 0.25), borderRadius: 3 }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.5}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.1em" }}
            >
              {t("ResumeWorkspaceHome.step2Eyebrow")}
            </Typography>
            <Typography variant="h6" fontWeight={500} sx={{ mt: 0.25 }}>
              {t("ResumeWorkspaceHome.roleTargetsHeading")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 520 }}>
              {t("ResumeWorkspaceHome.roleTargetsBody")}
            </Typography>
          </Box>
          {!open && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{
                flexShrink: 0,
                fontWeight: 500,
                textTransform: "none",
              }}
            >
              {t("ResumeWorkspaceHome.newTargetButton")}
            </Button>
          )}
        </Stack>

        {/* Inline create form */}
        {open && (
          <Box
            sx={{
              mt: 2.5,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2.5,
              bgcolor: alpha(theme.palette.primary.light, 0.04),
              border: 1,
              borderColor: alpha(theme.palette.primary.light, 0.18),
            }}
          >
            <Stack spacing={1.75}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.75}>
                <TextField
                  label={t("ResumeWorkspaceHome.companyLabel")}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={createMutation.isPending}
                />
                <TextField
                  label={t("ResumeWorkspaceHome.roleLabel")}
                  value={roleTitle}
                  onChange={(e) => setRoleTitle(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={createMutation.isPending}
                />
              </Stack>
              <TextField
                label={t("ResumeWorkspaceHome.jobDescriptionLabel")}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                fullWidth
                multiline
                minRows={5}
                maxRows={14}
                disabled={createMutation.isPending}
                placeholder={t("ResumeWorkspaceHome.jobDescriptionPlaceholder")}
                helperText={t("ResumeWorkspaceHome.jobDescriptionHelper", { length: jobDescription.trim().length, min: MIN_JD_CHARS })}
              />

              {isQuotaError ? (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => navigate("/pricing")}
                      sx={{ fontWeight: 500, textTransform: "none" }}
                    >
                      {t("ResumeWorkspaceHome.upgradeButton")}
                    </Button>
                  }
                >
                  {t("ResumeWorkspaceHome.quotaExceededMessage")}
                </Alert>
              ) : (
                createMutation.isError && (
                  <Alert severity="error">{targetErrorMessage(err, t)}</Alert>
                )
              )}

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setOpen(false);
                    createMutation.reset();
                  }}
                  disabled={createMutation.isPending}
                  sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}
                >
                  {t("ResumeWorkspaceHome.cancelButton")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreate}
                  disabled={!canCreate || createMutation.isPending}
                  startIcon={
                    createMutation.isPending ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : undefined
                  }
                  sx={{
                    fontWeight: 500,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  {createMutation.isPending ? t("ResumeWorkspaceHome.creatingButton") : t("ResumeWorkspaceHome.createButton")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Targets list */}
        {targets && targets.length > 0 ? (
          <Stack spacing={1.25} sx={{ mt: open ? 2.5 : 2 }}>
            {targets.map((tgt) => (
              <TargetRow
                key={tgt.id}
                target={tgt}
                onClick={() => navigate(`/resume-mastery/workspace/targets/${tgt.id}`)}
              />
            ))}
          </Stack>
        ) : (
          !open && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 1.5 }}>
                {t("ResumeWorkspaceHome.noTargetsYet")}
              </Typography>
            </>
          )
        )}
      </CardContent>
    </Card>
  );
}

function TargetRow({ target, onClick }: { target: ResumeTarget; onClick: () => void }) {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const statusColor = STATUS_COLORS[target.status] ?? "default";
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.75,
        borderRadius: 2,
        border: 1,
        borderColor: alpha(theme.palette.primary.light, 0.18),
        cursor: "pointer",
        transition: "all 0.16s ease",
        "&:hover": { borderColor: theme.palette.primary.light, bgcolor: alpha(theme.palette.primary.light, 0.04) },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" fontWeight={500} noWrap>
          {target.company}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {target.role_title}
        </Typography>
      </Box>
      {target.gap_score != null && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, color: theme.palette.primary.main, fontVariantNumeric: "tabular-nums" }}
        >
          {t("ResumeWorkspaceHome.readinessLabel", { score: 100 - target.gap_score })}
        </Typography>
      )}
      <Chip
        label={target.status}
        size="small"
        color={statusColor}
        sx={{ textTransform: "capitalize", fontWeight: 500 }}
      />
      <ArrowForwardIcon sx={{ fontSize: 18, color: alpha(theme.palette.primary.main, 0.6) }} />
    </Box>
  );
}
